import { dayjs } from "@thalamos/common";
const UK_TIMEZONE = "Europe/London";
const UK_DATE_TIME_FORMAT = "DD-MM-YYYY HH:mm";
/**
 * Calculates the original S136 end date (24 hours after start).
 *
 * @param detentionStartDateTime - ISO 8601 UTC string of the detention start time
 * @returns The end date formatted in UK local time (DD-MM-YYYY HH:mm),
 *          correctly handling BST/GMT transitions
 */
export function calculateOriginalS136EndDate(detentionStartDateTime) {
    return dayjs
        .utc(detentionStartDateTime)
        .add(24, "hour")
        .tz(UK_TIMEZONE)
        .format(UK_DATE_TIME_FORMAT);
}
/**
 * Calculates the extended S136 end date (24 hours + extension hours after start).
 *
 * @param detentionStartDateTime - ISO 8601 UTC string of the detention start time
 * @param extensionHours - Number of additional hours (1-12)
 * @returns The new end date formatted in UK local time (DD-MM-YYYY HH:mm),
 *          correctly handling BST/GMT transitions
 */
export function calculateExtendedS136EndDate(detentionStartDateTime, extensionHours) {
    const totalHours = 24 + extensionHours;
    return dayjs
        .utc(detentionStartDateTime)
        .add(totalHours, "hour")
        .tz(UK_TIMEZONE)
        .format(UK_DATE_TIME_FORMAT);
}
/**
 * The whole-hour Section 136 extension currently in effect, inferred from the
 * detention window. `0` means the standard 24h with no extension; `1`–`12` means
 * extended by that many hours. Returns `undefined` when the window is shorter than
 * 24h or is not a whole number of hours beyond it, i.e. the extension cannot be
 * determined.
 *
 * @param detentionStartDateTime - ISO 8601 string of the detention start time
 * @param expiryDateTime - ISO 8601 string of the detention end time
 */
export function deriveSection136ExtensionHours(detentionStartDateTime, expiryDateTime) {
    const totalMinutes = dayjs(expiryDateTime).diff(dayjs(detentionStartDateTime), "minute");
    if (totalMinutes < 24 * 60)
        return undefined;
    const extraMinutes = totalMinutes - 24 * 60;
    if (extraMinutes % 60 !== 0)
        return undefined;
    return extraMinutes / 60;
}
/**
 * Whether a Section 136 detention window is longer than the standard 24h
 * baseline — i.e. an extension is in effect — regardless of whether the extra
 * time is a whole number of hours. Use this to detect that an extension was
 * present (e.g. before it is cleared), where {@link deriveSection136ExtensionHours}
 * is too strict: that returns `undefined` for a non-whole-hour window.
 *
 * @param detentionStartDateTime - ISO 8601 string of the detention start time
 * @param expiryDateTime - ISO 8601 string of the detention end time
 */
export function isSection136Extended(detentionStartDateTime, expiryDateTime) {
    return (dayjs(expiryDateTime).diff(dayjs(detentionStartDateTime), "minute") >
        24 * 60);
}
//# sourceMappingURL=s136DateTime.js.map